import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Avatar,
  Tooltip,
  Modal,
  Descriptions,
} from "antd";
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaBuilding,
  FaHourglassHalf,
  FaEye,
} from "react-icons/fa";
import { MdMeetingRoom } from "react-icons/md";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../../api/Api";
import dayjs from "dayjs";

const { Content } = Layout;
const { Title, Text } = Typography;
const { confirm } = Modal;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return str;
  }
};

const formatTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleTimeString("pt-PT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return str;
  }
};

const calcDuracao = (entrada, saida) => {
  if (!entrada || !saida) return "—";
  const diff = dayjs(saida).diff(dayjs(entrada), "minute");
  if (diff < 60) return `${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE DETALHES
───────────────────────────────────────────────────────────────────────────── */
const DetalhesModal = ({ open, onClose, pedido, onAprovar, onRejeitar, loading }) => {
  if (!pedido) return null;

  const isPendente = pedido.aprovado !== "S" && pedido.aprovado !== "N";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={560}
      title={
        <Space>
          <FaEye style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Detalhes do Pedido</span>
        </Space>
      }
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
        // Botões de ação só aparecem para pendentes
        isPendente && (
          <Button
            key="rejeitar"
            danger
            icon={<FaTimesCircle />}
            onClick={() => onRejeitar(pedido)}
            loading={loading}
          >
            Rejeitar
          </Button>
        ),
        isPendente && (
          <Button
            key="aprovar"
            type="primary"
            icon={<FaCheckCircle />}
            onClick={() => onAprovar(pedido)}
            loading={loading}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Aprovar
          </Button>
        ),
      ]}
      bodyStyle={{ padding: "24px" }}
    >
      {/* Sala destaque */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f4c81 0%, #1677ff 100%)",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.2)",
            borderRadius: 10,
            padding: 10,
          }}
        >
          <MdMeetingRoom style={{ fontSize: 28, color: "#fff" }} />
        </div>
        <div>
          <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>Sala solicitada</Text>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{pedido.sala}</div>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            <FaBuilding style={{ marginRight: 4 }} />
            {pedido.escritorio}
          </Text>
        </div>
      </div>

      <Descriptions column={2} bordered size="small" labelStyle={{ fontWeight: 500, width: 120 }}>
        <Descriptions.Item label="Solicitante" span={2}>
          <Space>
            <Avatar size="small" style={{ backgroundColor: "#1677ff" }}>
              {pedido.solicitante?.[0]}
            </Avatar>
            {pedido.solicitante}
          </Space>
        </Descriptions.Item>
                <Descriptions.Item label="Tarefa" span={2}>
          <Space>
            {pedido.titulo}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Data">
          {dayjs(pedido.entrada).format("DD/MM/YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label="Duração">
          {calcDuracao(pedido.entrada, pedido.saida)}
        </Descriptions.Item>
        <Descriptions.Item label="Entrada">{formatTime(pedido.entrada)}</Descriptions.Item>
        <Descriptions.Item label="Saída">{formatTime(pedido.saida)}</Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   APROVAÇÃO PAGE
───────────────────────────────────────────────────────────────────────────── */
const AprovacaoSala = () => {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [detalhesPedido, setDetalhesPedido] = useState(null);
  const [detalhesOpen, setDetalhesOpen] = useState(false);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Aprovação de Salas | Dashboard";
    loadPedidos();
  }, []);

  const loadPedidos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Api.ListLogisticaSalaAprovacao();
      setPedidos(Array.isArray(res) ? res : []);
    } catch {
      toast.error("Erro ao carregar pedidos.");
      setPedidos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAprovar = (pedido) => {
    confirm({
      title: "Confirmar Aprovação",
      icon: <FaCheckCircle style={{ color: "#52c41a", marginRight: 8 }} />,
      content: (
        <div>
          <p>
            Deseja aprovar a reserva de <strong>{pedido.sala}</strong> para{" "}
            <strong>{pedido.solicitante}</strong>?
          </p>
          <p style={{ color: "#8c8c8c", fontSize: 13 }}>
            {dayjs(pedido.entrada).format("DD/MM/YYYY")} • {formatTime(pedido.entrada)} →{" "}
            {formatTime(pedido.saida)}
          </p>
        </div>
      ),
      okText: "Aprovar",
      okType: "primary",
      okButtonProps: { style: { backgroundColor: "#52c41a", borderColor: "#52c41a" } },
      cancelText: "Cancelar",
      onOk: () => executarAcao(pedido, "S"),
    });
  };

  const handleRejeitar = (pedido) => {
    confirm({
      title: "Confirmar Rejeição",
      icon: <ExclamationCircleOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div>
          <p>
            Deseja rejeitar a reserva de <strong>{pedido.sala}</strong> para{" "}
            <strong>{pedido.solicitante}</strong>?
          </p>
        </div>
      ),
      okText: "Rejeitar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => executarAcao(pedido, "N"),
    });
  };

  const executarAcao = async (pedido, aprovado) => {
    try {
      setActionLoading(true);
      await Api.AlterLogisticaSala({
        id: pedido.id,
        entrada: pedido.entrada,
        saida: pedido.saida,
        id_sala: pedido.id_sala,
        aprovado,
      });
      if (aprovado === "S") {
        toast.success("Pedido aprovado com sucesso!");
      } else {
        toast.warning("Pedido rejeitado.");
      }
      setDetalhesOpen(false);
      loadPedidos();
    } catch {
      toast.error("Erro ao processar pedido.");
    } finally {
      setActionLoading(false);
    }
  };

  const openDetalhes = (pedido) => {
    setDetalhesPedido(pedido);
    setDetalhesOpen(true);
  };

  const filtered = pedidos.filter((p) => {
    const q = search.toLowerCase();
    return (
      !q ||
      p.solicitante?.toLowerCase().includes(q) ||
      p.sala?.toLowerCase().includes(q) ||
      p.escritorio?.toLowerCase().includes(q)
    );
  });

  const countHoje = pedidos.filter((p) =>
    dayjs(p.entrada).isSame(dayjs(), "day")
  ).length;

  const columns = [
    {
      title: "Sala / Escritório",
      key: "sala",
      width: 280,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{ backgroundColor: "#ffffff", color: "#1677ff", borderRadius: 8 }}
            icon={<MdMeetingRoom />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>{record.sala}</div>
            <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <FaBuilding /> {record.escritorio}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Solicitante / Tarefa",
      dataIndex: "solicitante",
      key: "solicitante",
       width: 280,
     render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>{record.solicitante}</div>
            <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
             {record.titulo}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Data",
      key: "data",
      width: 120,
      render: (_, r) => (
        <Text>
          <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
          {dayjs(r.entrada).format("DD/MM/YYYY")}
        </Text>
      ),
    },
    {
      title: "Horário",
      key: "horario",
      width: 130,
      render: (_, r) => (
        <Text>
          {formatTime(r.entrada)}{" "}
          <span style={{ color: "#8c8c8c" }}>→</span>{" "}
          {formatTime(r.saida)}
        </Text>
      ),
    },
    {
      title: "Duração",
      key: "duracao",
      width: 100,
      render: (_, r) => (
        <Tag color="blue" style={{ borderRadius: 20 }}>
          {calcDuracao(r.entrada, r.saida)}
        </Tag>
      ),
    },
    {
      title: "Estado",
      dataIndex: "aprovado",
      key: "aprovado",
      width: 120,
      render: (aprovado) => {
        if (aprovado === "S") {
          return (
            <Tag color="success" icon={<FaCheckCircle />} style={{ borderRadius: 20, padding: "4px 12px", fontWeight: 500 }}>
              Aprovado
            </Tag>
          );
        }
        if (aprovado === "N") {
          return (
            <Tag color="error" icon={<FaTimesCircle />} style={{ borderRadius: 20, padding: "4px 12px", fontWeight: 500 }}>
              Rejeitado
            </Tag>
          );
        }
        // "P", null, undefined → Pendente
        return (
          <Tag color="warning" icon={<FaHourglassHalf />} style={{ borderRadius: 20, padding: "4px 12px", fontWeight: 500 }}>
            Pendente
          </Tag>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (_, record) => {
        const isPendente = record.aprovado !== "S" && record.aprovado !== "N";
        return (
          <Space size="small">
            <Tooltip title="Ver detalhes">
              <Button
                type="text"
                icon={<FaEye />}
                onClick={() => openDetalhes(record)}
                style={{ color: "#1677ff" }}
              />
            </Tooltip>
            {isPendente && (
              <>
                <Tooltip title="Aprovar">
                  <Button
                    type="text"
                    icon={<FaCheckCircle />}
                    onClick={() => handleAprovar(record)}
                    style={{ color: "#52c41a" }}
                  />
                </Tooltip>
                <Tooltip title="Rejeitar">
                  <Button
                    type="text"
                    icon={<FaTimesCircle />}
                    onClick={() => handleRejeitar(record)}
                    style={{ color: "#ff0000" }}
                    danger
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(212, 107, 8, 0.2)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaHourglassHalf style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Aprovação de Salas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Gerencie os pedidos de reserva de salas de reunião
                  </Text>
                </div>
              </Space>
            </Col>
 
          </Row>
        </div>

        {/* Stats */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Pedidos Pendentes</span>}
                value={pedidos.filter(p => p.aprovado !== "S" && p.aprovado !== "N").length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div style={{ backgroundColor: "#ffffff", borderRadius: 12, padding: 10, marginRight: 16 }}>
                    <FaHourglassHalf style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Para Hoje</span>}
                value={countHoje}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div style={{ backgroundColor: "#ffffff", borderRadius: 12, padding: 10, marginRight: 16 }}>
                    <FaCalendarAlt style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Salas Solicitadas</span>}
                value={[...new Set(pedidos.map((p) => p.id_sala))].length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div style={{ backgroundColor: "#ffffff", borderRadius: 12, padding: 10, marginRight: 16 }}>
                    <MdMeetingRoom style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Search */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Input
            size="large"
            prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
            placeholder="Pesquisar por solicitante, sala ou escritório..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ borderRadius: 30, maxWidth: 500 }}
          />
        </Card>

        {/* Table */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)", overflow: "hidden" }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} pedidos`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhum pedido pendente" }}
            scroll={{ x: 1000 }}
            rowClassName={(record) => {
              const hoje = dayjs(record.entrada).isSame(dayjs(), "day");
              return hoje ? "row-hoje" : "";
            }}
          />
        </Card>

        {/* Modal detalhes */}
        <DetalhesModal
          open={detalhesOpen}
          onClose={() => setDetalhesOpen(false)}
          pedido={detalhesPedido}
          onAprovar={handleAprovar}
          onRejeitar={handleRejeitar}
          loading={actionLoading}
        />
      </Content>
    </Layout>
  );
};

export default AprovacaoSala;